import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { authStateSelector } from '../../selectors/global';
import { UserRequiredRegistrationToEventModal } from '../components/UserRequiredRegistrationToEventModal';
import { TRequiredRegistrationEvent } from '../../../../types/eventTypes';
import moment from 'moment';
import { safeParseJson } from '../../../../common/utils';
import _ from 'lodash';
import { canUseDOM } from 'exenv';

const LOCAL_STORAGE_KEY = 'rioMutedEvents';
const DEFAULT_DELAY_IN_MINUTES = 15;
const MUTED_FROM_USER_DELAY_IN_MINUTES = 1440;
const CHECK_EVENTS_DELAY_IN_MINUTES = 2;

interface MutedEvent {
  id: string;
  muteUntil: string;
}

const findUnmutedEvent = (requiredEvents: TRequiredRegistrationEvent[]): TRequiredRegistrationEvent | null => {
  if (!canUseDOM) {
    return null;
  }

  const mutedEventsJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (mutedEventsJSON) {
    let mutedEvents = safeParseJson<MutedEvent[]>(mutedEventsJSON);
    if (!_.isArray(mutedEvents)) {
      // ensure we always have an array so downstream code doesn't break
      mutedEvents = [];
    }

    const currentTime = moment.utc();

    for (const eventData of requiredEvents) {
      const isMuted = mutedEvents.some(
        (event) => event.id === eventData.event.id.toString() && moment.utc(event.muteUntil).isAfter(currentTime)
      );
      if (!isMuted) {
        return eventData;
      }
    }
  } else {
    return requiredEvents[0];
  }

  return null;
};

const muteEvent = (eventId: number, durationInMinutes: number): void => {
  const currentTime = moment.utc();
  const muteUntil = currentTime.clone().add(durationInMinutes, 'minutes').toISOString();
  const mutedEventsJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
  let mutedEvents: MutedEvent[] = [];

  if (mutedEventsJSON) {
    mutedEvents = JSON.parse(mutedEventsJSON);

    const existingEventIndex = mutedEvents.findIndex((event) => event.id === eventId.toString());

    if (existingEventIndex !== -1) {
      mutedEvents[existingEventIndex].muteUntil = muteUntil;
    } else {
      mutedEvents.push({ id: eventId.toString(), muteUntil });
    }
  } else {
    mutedEvents.push({ id: eventId.toString(), muteUntil });
  }

  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(mutedEvents));
};

export const EventRegistrationProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const auth = useSelector(authStateSelector);
  const requiredRegistrationEvents = auth?.requiredRegistrationEvents || [];

  const [requiredRegistrationEvent, setRequiredRegistrationEvent] = useState<TRequiredRegistrationEvent | null>(
    findUnmutedEvent(requiredRegistrationEvents)
  );

  const handleMuteEvent = (id: number, isLong?: boolean) => {
    if (requiredRegistrationEvent) {
      muteEvent(id, isLong ? MUTED_FROM_USER_DELAY_IN_MINUTES : DEFAULT_DELAY_IN_MINUTES);
    }
  };

  useEffect(() => {
    const checkMuteDates = () => {
      const updatedRequiredRegistrationEvent = findUnmutedEvent(requiredRegistrationEvents);
      if (updatedRequiredRegistrationEvent) {
        setRequiredRegistrationEvent(updatedRequiredRegistrationEvent);
      }
    };

    const intervalId = setInterval(checkMuteDates, CHECK_EVENTS_DELAY_IN_MINUTES * 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [requiredRegistrationEvents, requiredRegistrationEvent]);

  return (
    <>
      {requiredRegistrationEvent && (
        <UserRequiredRegistrationToEventModal
          requiredRegistrationEvent={requiredRegistrationEvent}
          onMute={handleMuteEvent}
        />
      )}
      {children}
    </>
  );
};
